import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { errorDeApi } from '../api/http';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

type Tono = 'exito' | 'aviso' | 'error';

interface Mensaje {
  id: number;
  texto: string;
  tono: Tono;
}

interface ValorAvisos {
  /** Confirma que algo ocurrio. El texto va en pasado y nombra la accion. */
  confirmar(texto: string): void;
  advertir(texto: string): void;
  /** Un error se queda hasta que se cierra: se lee, no se adivina. */
  fallar(textoOError: string | unknown): void;
}

const Contexto = createContext<ValorAvisos>({
  confirmar: () => {},
  advertir: () => {},
  fallar: () => {},
});

export function useAvisos(): ValorAvisos {
  return useContext(Contexto);
}

const SEVERIDAD: Record<Tono, 'success' | 'warning' | 'error'> = {
  exito: 'success',
  aviso: 'warning',
  error: 'error',
};

/** Rol semantico de cada tono (`--status-<rol>-*`). */
const ROL: Record<Tono, 'success' | 'warning' | 'danger'> = {
  exito: 'success',
  aviso: 'warning',
  error: 'danger',
};

/**
 * Confirmaciones efimeras.
 *
 * Existe porque hasta ahora guardar no decia nada: el boton volvia a su sitio
 * y el usuario se quedaba sin saber si el documento quedo firmado. En un
 * expediente clinico esa duda se paga capturando dos veces.
 *
 * Regla de redaccion: el aviso usa el MISMO verbo que el boton que lo produjo,
 * en pasado. El boton dice «Firmar y guardar»; el aviso dice «Estudio firmado».
 * Asi se aprende el vocabulario del producto sin tener que explicarlo.
 */
export function ProveedorDeAvisos({ children }: { children: ReactNode }) {
  const [cola, setCola] = useState<Mensaje[]>([]);

  const empujar = useCallback((texto: string, tono: Tono) => {
    setCola((previa) => [...previa, { id: Date.now() + previa.length, texto, tono }]);
  }, []);

  const valor = useMemo<ValorAvisos>(
    () => ({
      confirmar: (texto) => empujar(texto, 'exito'),
      advertir: (texto) => empujar(texto, 'aviso'),
      fallar: (textoOError) =>
        empujar(
          typeof textoOError === 'string' ? textoOError : errorDeApi(textoOError).mensaje,
          'error',
        ),
    }),
    [empujar],
  );

  const actual = cola[0];
  const lavado = `var(--status-${ROL[actual?.tono ?? 'exito']}-wash)`;

  return (
    <Contexto.Provider value={valor}>
      {children}
      <Snackbar
        // Se ancla abajo a la izquierda: la accion principal vive abajo a la
        // derecha, y un aviso que tapa el boton que acaba de pulsarse estorba.
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={Boolean(actual)}
        // El exito se va solo; el error se queda hasta que se cierra.
        autoHideDuration={actual?.tono === 'error' ? null : 4000}
        // En movil, por encima de la barra inferior de navegacion.
        sx={{ bottom: { xs: 'calc(72px + env(safe-area-inset-bottom))', md: 24 } }}
        onClose={(_evento, razon) => {
          if (razon === 'clickaway') return;
          setCola((previa) => previa.slice(1));
        }}
        // La clave fuerza el remonte cuando llega otro aviso mientras hay uno
        // visible; sin ella el segundo hereda el temporizador del primero.
        key={actual?.id}
      >
        {/* Insignia de estado, no bloque macizo: lavado + texto + borde del
            rol, sobre superficie opaca (el lavado es translucido en oscuro).
            El icono de MUI da la forma —✓ en exito, triangulo en aviso,
            circulo en error— para que el estado no dependa del color. */}
        <Alert
          severity={actual ? SEVERIDAD[actual.tono] : 'success'}
          variant="standard"
          onClose={() => setCola((previa) => previa.slice(1))}
          sx={{
            alignItems: 'center',
            minWidth: { sm: 320 },
            maxWidth: 'min(92vw, 480px)',
            background: `linear-gradient(${lavado}, ${lavado}), var(--bg-raised)`,
            boxShadow: 'var(--shadow-3)',
            '& .MuiAlert-action': { pt: 0 },
          }}
        >
          {actual?.texto}
        </Alert>
      </Snackbar>
    </Contexto.Provider>
  );
}
